import React, { useState, useEffect } from "react";
import { Trophy, Medal, FileDown, Search } from "lucide-react";
import * as api from "../../api";

/* -----------------------------------------------------------------------------
 * TopUsersRanking — admin tab. Fetches /api/admin/top-users and displays a
 * searchable, filterable, exportable leaderboard of clients by cumulative
 * completed spend.
 * --------------------------------------------------------------------------- */
export function TopUsersRanking() {
  const [range, setRange] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getAdminTopUsers(range, 500).then((res) => {
      if (!cancelled) setRows(Array.isArray(res?.items) ? res.items : []);
    }).catch((e) => {
      if (!cancelled) setError(e.message || String(e));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [range]);

  const filtered = query.trim()
    ? rows.filter((r) => {
        const q = query.trim().toLowerCase();
        return (
          (r.name || "").toLowerCase().includes(q) ||
          (r.username || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q) ||
          (r.phone_number || "").toLowerCase().includes(q) ||
          (r.ci || "").toLowerCase().includes(q)
        );
      })
    : rows;

  const fmtBs = (n) => new Intl.NumberFormat("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" });
  };

  const medalStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-br from-amber-400 to-yellow-600 text-black border-yellow-300";
    if (rank === 2) return "bg-gradient-to-br from-slate-300 to-slate-500 text-black border-slate-200";
    if (rank === 3) return "bg-gradient-to-br from-amber-700 to-orange-800 text-white border-amber-500";
    return null;
  };

  const exportCsv = () => {
    const header = ["Rank", "Nombre", "Usuario", "Email", "Teléfono", "Cédula", "Rol", "Recargas", "Total Bs", "Total USD", "Última Compra"];
    const csvEscape = (v) => {
      const s = String(v ?? "");
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.map(csvEscape).join(",")];
    filtered?.forEach((r) => {
      lines.push([
        r.rank,
        r.name,
        r.username,
        r.email,
        r.phone_number,
        r.ci,
        r.role,
        r.orders_count,
        (Number(r.total_bs) || 0).toFixed(2),
        (Number(r.total_usd) || 0).toFixed(2),
        r.last_purchase || "",
      ].map(csvEscape).join(","));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.href = URL.createObjectURL(blob);
    link.download = `top-users-${range}-${ts}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const rangeButtons = [
    { id: "all", label: "Histórico" },
    { id: "month", label: "Este Mes" },
    { id: "7d", label: "Últimos 7 días" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-top-users-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-amber-400" fill="currentColor" strokeWidth={0} />
          </div>
          <div>
            <h3 className="text-lg font-black text-white tracking-wide">RANKING DE CLIENTES</h3>
            <p className="text-[11px] text-zinc-400 font-mono">Ordenado por total gastado en pedidos aprobados</p>
          </div>
        </div>
        <button
          onClick={exportCsv}
          data-testid="top-users-export-csv-btn"
          disabled={filtered.length === 0}
          className="px-4 py-2 rounded-xl text-xs font-mono font-bold bg-green-600/20 border border-green-500/40 text-green-300 hover:bg-green-600/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <FileDown className="w-3.5 h-3.5" /> Exportar CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {rangeButtons.map((b) => (
          <button
            key={b.id}
            onClick={() => setRange(b.id)}
            data-testid={`top-users-range-${b.id}`}
            className={`px-3 py-1.5 text-xs font-mono font-bold rounded-lg border ${
              range === b.id
                ? "bg-cyan-600/20 border-cyan-500/50 text-cyan-300"
                : "bg-ag7-bg border-[var(--ag7-border-soft)] text-zinc-400 hover:text-white hover:border-ag7-purple/40"
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, usuario, email, teléfono o cédula…"
          data-testid="top-users-search-input"
          className="w-full pl-10 pr-3 py-2 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl text-white text-xs focus:outline-none focus:border-cyan-500"
        />
      </div>

      <div className="rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-xs text-zinc-500 font-mono">Cargando ranking…</div>
        )}
        {error && !loading && (
          <div className="p-8 text-center text-xs text-red-400 font-mono" data-testid="top-users-error">
            Error: {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-xs text-zinc-500 font-mono" data-testid="top-users-empty">
            {query ? "Ningún cliente coincide con la búsqueda." : "No hay pedidos completados en este rango de fechas."}
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="top-users-table">
              <thead className="bg-ag7-bg/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <tr className="border-b border-[var(--ag7-border)]">
                  <th className="px-3 py-3 text-left">#</th>
                  <th className="px-3 py-3 text-left">Cliente</th>
                  <th className="px-3 py-3 text-left hidden md:table-cell">Contacto</th>
                  <th className="px-3 py-3 text-right">Recargas</th>
                  <th className="px-3 py-3 text-right">Total Gastado</th>
                  <th className="px-3 py-3 text-right hidden sm:table-cell">Última Compra</th>
                </tr>
              </thead>
              <tbody>
                {filtered?.map((r) => {
                  const medalCls = medalStyle(r.rank);
                  return (
                    <tr key={r.user_id || r.rank} className="border-b border-[var(--ag7-border)]/60 hover:bg-ag7-surface-2/40" data-testid={`top-users-row-${r.rank}`}>
                      <td className="px-3 py-3">
                        {medalCls ? (
                          <span
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-black text-xs shadow ${medalCls}`}
                            title={`Puesto ${r.rank}`}
                          >
                            <Medal className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-ag7-surface-2 border border-[var(--ag7-border-soft)] text-zinc-400 font-mono font-bold text-xs">
                            {r.rank}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-bold text-white">
                            {r.name || r.username || (r.email ? r.email.split("@")[0] : "—")}
                          </span>
                          <span className="text-[10px] text-zinc-500 font-mono">
                            {r.username ? `@${r.username}` : (r.email || "—")}
                          </span>
                          {r.role && r.role !== "client" && (
                            <span className="inline-flex w-fit text-[9px] px-1.5 py-0.5 rounded bg-purple-950/60 border border-purple-500/40 text-purple-300 uppercase font-mono">
                              {r.role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-zinc-400 font-mono hidden md:table-cell">
                        <div className="flex flex-col gap-0.5">
                          <span>{r.phone_number || "—"}</span>
                          {r.ci && <span className="text-[10px] text-zinc-500">CI: {r.ci}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="inline-flex px-2 py-0.5 rounded bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 font-mono font-bold">
                          {r.orders_count}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex flex-col items-end">
                          <span className="text-green-400 font-mono font-black text-sm">Bs {fmtBs(r.total_bs)}</span>
                          <span className="text-[10px] text-zinc-500 font-mono">${(Number(r.total_usd) || 0).toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-zinc-400 font-mono hidden sm:table-cell">
                        {fmtDate(r.last_purchase)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-ag7-bg/60">
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-right text-[10px] font-mono text-zinc-500">
                    Mostrando {filtered.length} de {rows.length} clientes
                    {query && ` · filtro: "${query}"`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
